import React, { FC, useMemo } from 'react';

import { CardSchema } from 'constants/graphqlTypes';
import Avatar from 'components/UI/Avatar';
import UILink from 'components/UI/Link';
import { getGradientImage } from 'components/Sparks/constants';
import { transformRoute } from 'helpers/routingHelper';
import { ROUTE_SPARK_DETAIL } from 'routes';

import { ReactComponent as SparkIcon } from 'assets/icons/Spark.svg';

import {
  StyledSparkCard,
  StyledUserRow,
  StyledAvatarWrap,
  StyledThoughtText,
  StyledCover,
  StyledCoverVideo,
  StyledFooter,
  StyledSparkButton,
} from './styled';

interface FeaturedSparkCardProps {
  card: CardSchema;
  onSparkButtonClick?: (categorySlug?: string) => void;
}

const FeaturedSparkCard: FC<FeaturedSparkCardProps> = ({ card, onSparkButtonClick }) => {
  const { user, title, description, cardStyle } = card;

  // Use description if present, otherwise fall back to title
  const displayText = description || title;

  // Type assertion for cover selection
  const typedCard = card as typeof card & {
    userCoverSelection?: {
      mediaUrl?: string;
      mediaType?: string;
      thumbnailUrl?: string;
    };
  };

  const coverMediaUrl = typedCard.userCoverSelection?.mediaUrl;
  const thumbnailUrl = typedCard.userCoverSelection?.thumbnailUrl;
  const isAnimatedCover = typedCard.userCoverSelection?.mediaType === 'ANIMATION';
  const predefinedGradientName = cardStyle?.bgColor?.predefinedGradientName;

  // Background image: thumbnail for animations, cover for images, or gradient fallback
  const backgroundImage = useMemo(() => {
    if (isAnimatedCover && thumbnailUrl) {
      return thumbnailUrl;
    }
    if (coverMediaUrl && !isAnimatedCover) {
      return coverMediaUrl;
    }
    return getGradientImage(predefinedGradientName ?? '');
  }, [isAnimatedCover, thumbnailUrl, coverMediaUrl, predefinedGradientName]);

  if (!user) {
    return null;
  }

  const sparkRoute = transformRoute(ROUTE_SPARK_DETAIL, { sparkId: card.id });

  return (
    <StyledSparkCard>
      {/* User row with avatar and thought */}
      <StyledUserRow>
        <StyledAvatarWrap>
          <Avatar user={user} dimension={20} />
        </StyledAvatarWrap>
        <StyledThoughtText>{displayText ? `"${displayText}"` : ''}</StyledThoughtText>
      </StyledUserRow>

      {/* Cover image/video */}
      <UILink route={sparkRoute}>
        <StyledCover bgImage={!isAnimatedCover ? backgroundImage : thumbnailUrl}>
          {isAnimatedCover && coverMediaUrl && <StyledCoverVideo src={coverMediaUrl} autoPlay loop muted playsInline />}
        </StyledCover>
      </UILink>

      {/* Footer with spark icon - opens create spark modal */}
      <StyledFooter>
        <StyledSparkButton
          type="button"
          aria-label="Create Spark"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onSparkButtonClick?.(card.categorySlug ?? undefined);
          }}
        >
          <SparkIcon />
        </StyledSparkButton>
      </StyledFooter>
    </StyledSparkCard>
  );
};

export default FeaturedSparkCard;
